import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Tags, Briefcase, Calendar, Star, Users,
  BarChart3, ShieldCheck, LogOut, Menu, X, Lock, Inbox,
} from "lucide-react";
import { ADMIN_NAV } from "@admin/constants/permissions";
import { usePermissions } from "@admin/hooks/usePermissions";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_BASE, ADMIN_LOGIN_PATH } from "@admin/constants/route";

const ICONS = { LayoutDashboard, Tags, Briefcase, Calendar, Star, Users, BarChart3, ShieldCheck, Lock, Inbox };

function SidebarItem({ to, label, icon, exact }) {
  const Icon = ICONS[icon] || LayoutDashboard;
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
          isActive
            ? "bg-[#D4AF37] text-[#0A2540] shadow"
            : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function AdminLayout({ children }) {
  const perms = usePermissions();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const visibleNav = ADMIN_NAV.filter((n) => !n.permission || perms.has(n.permission));

  const initials = (user?.name || user?.email || "A")
    .split(" ").slice(-2).map((x) => x[0]).join("").toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate(ADMIN_LOGIN_PATH);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#0A2540] text-white px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen(!open)} aria-label="Toggle sidebar" data-testid="admin-sidebar-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span className="font-bold tracking-wide" style={{ fontFamily: "Poppins" }}>Advoset Admin</span>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold">RBAC</span>
      </header>

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#0A2540] text-white p-4 z-40 transition-transform lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="hidden lg:flex items-center gap-2 px-2 py-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-[#D4AF37] grid place-items-center text-[#0A2540] font-extrabold">A</div>
          <div className="leading-tight">
            <p className="font-extrabold tracking-tight" style={{ fontFamily: "Poppins" }}>Advoset Admin</p>
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Legal Help, Simplified</p>
          </div>
        </div>

        <nav className="space-y-1">
          {visibleNav.map((item) => (
            <SidebarItem key={item.to} {...item} />
          ))}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#D4AF37] text-[#0A2540] grid place-items-center font-extrabold text-sm">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" data-testid="admin-current-user-name">{user?.name || user?.email}</p>
              <p className="text-[10px] text-slate-400 truncate">{(user?.admin_roles || []).join(", ") || "admin"}</p>
            </div>
            <button onClick={handleLogout} className="text-slate-300 hover:text-white" title="Sign out" data-testid="admin-logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop on mobile */}
      {open && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto" data-testid="admin-main">
          {children}
        </div>
      </main>
    </div>
  );
}
